import { type ReactNode } from 'react';
import { Icon } from './Icon';
import { IconButton } from './primitives';

const cx = (...parts: (string | false | undefined)[]) => parts.filter(Boolean).join(' ');

/* ---------- Dialog ---------- */
export interface DialogProps {
  open?: boolean;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
  width?: number;
}

export function Dialog({ open = true, title, children, footer, onClose, width = 460 }: DialogProps) {
  if (!open) return null;
  return (
    <div className="zen-scrim" onClick={onClose}>
      <div
        className="zen-dialog"
        style={{ maxWidth: width }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="zen-dialog__head">
          <span className="zen-dialog__title">{title}</span>
          {onClose && <IconButton icon="x" label="Close" size="sm" onClick={onClose} />}
        </div>
        <div className="zen-dialog__body">{children}</div>
        {footer && <div className="zen-dialog__foot">{footer}</div>}
      </div>
    </div>
  );
}

/* ---------- Toast ---------- */
type Tone = 'info' | 'success' | 'warning' | 'danger';
const TOAST_ICONS: Record<Tone, string> = { info: 'info', success: 'check-circle', warning: 'alert-triangle', danger: 'alert-circle' };
const TOAST_COLORS: Record<Tone, string> = {
  info: 'var(--status-info)',
  success: 'var(--status-success)',
  warning: 'var(--status-warning)',
  danger: 'var(--status-danger)',
};

export interface ToastProps {
  tone?: Tone;
  title?: ReactNode;
  children?: ReactNode;
  onDismiss?: () => void;
}

export function Toast({ tone = 'info', title, children, onDismiss }: ToastProps) {
  return (
    <div className={`zen-toast zen-toast--${tone}`} role="status">
      <Icon name={TOAST_ICONS[tone]} size="18px" style={{ color: TOAST_COLORS[tone], marginTop: 1 }} />
      <div style={{ flex: 1 }}>
        {title && <div className="zen-toast__title">{title}</div>}
        <div className="zen-toast__msg">{children}</div>
      </div>
      {onDismiss && <IconButton icon="x" label="Dismiss" size="sm" onClick={onDismiss} />}
    </div>
  );
}

/* ---------- Tooltip ---------- */
export function Tooltip({ label, placement = 'top', children }: { label: ReactNode; placement?: 'top' | 'bottom'; children: ReactNode }) {
  return (
    <span className="zen-tip">
      {children}
      <span className={cx('zen-tip__bubble', placement === 'bottom' && 'zen-tip__bubble--bottom')}>{label}</span>
    </span>
  );
}

/* ---------- EmptyState ---------- */
export function EmptyState({
  icon = 'waves',
  title,
  children,
  action,
}: {
  icon?: string;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="zen-empty">
      <Icon name={icon} size="28px" style={{ color: 'var(--navy-300)' }} />
      {title && <div className="zen-empty__title">{title}</div>}
      {children && <div className="zen-empty__body">{children}</div>}
      {action}
    </div>
  );
}
